/**
 * UI store (Zustand) — ephemeral client/UI state: locale + document dir, density (Confort/Compact),
 * the side-nav collapse, the command-palette open flag, and the online/offline flag. NONE of this
 * is server data. Locale + density persist to localStorage (non-sensitive UX prefs only).
 */
import { create } from "zustand";

export type Locale = "fr" | "ar";
export type Dir = "ltr" | "rtl";
export type Density = "confort" | "compact";

const LS_LOCALE = "nv.locale";
const LS_DENSITY = "nv.density";

const dirForLocale = (l: Locale): Dir => (l === "ar" ? "rtl" : "ltr");

function readStoredLocale(): Locale {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(LS_LOCALE) : null;
  return v === "ar" ? "ar" : "fr";
}
function readStoredDensity(): Density {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(LS_DENSITY) : null;
  return v === "compact" ? "compact" : "confort";
}

interface UiState {
  locale: Locale;
  dir: Dir;
  density: Density;
  navCollapsed: boolean;
  paletteOpen: boolean;
  online: boolean;

  setLocale: (locale: Locale) => void;
  setDensity: (density: Density) => void;
  toggleNav: () => void;
  setPaletteOpen: (open: boolean) => void;
  setOnline: (online: boolean) => void;
}

export const useUiStore = create<UiState>((set) => {
  const locale = readStoredLocale();
  return {
    locale,
    dir: dirForLocale(locale),
    density: readStoredDensity(),
    navCollapsed: false,
    paletteOpen: false,
    online: typeof navigator !== "undefined" ? navigator.onLine : true,

    setLocale: (next) => {
      if (typeof localStorage !== "undefined") localStorage.setItem(LS_LOCALE, next);
      set({ locale: next, dir: dirForLocale(next) });
    },
    setDensity: (density) => {
      if (typeof localStorage !== "undefined") localStorage.setItem(LS_DENSITY, density);
      set({ density });
    },
    toggleNav: () => set((s) => ({ navCollapsed: !s.navCollapsed })),
    setPaletteOpen: (paletteOpen) => set({ paletteOpen }),
    setOnline: (online) => set({ online }),
  };
});

/** Reflect locale/dir onto <html> (call from a top-level effect). */
export function applyDocumentLocale(locale: Locale, dir: Dir): void {
  const html = document.documentElement;
  html.setAttribute("lang", locale);
  html.setAttribute("dir", dir);
}
